import { useEffect, useState, type VoidFunctionComponent } from 'react';

import type { LatestRevision } from '../../../../shared/projectsV2';
import { compactRevision } from '../../../helpers/revisions';
import { Select } from '..';
import styles from './BranchRevisionCard.module.less';
import type { SelectOption } from '../../../types';
import { selectOptionFactory } from '../../../utils';
import { useDebouncedCallback } from '../../../hooks';
import useFetchBranches from '../../../hooks/deprecatedAPI/useFetchBranches/useFetchBranches';
import useFetchRevisions from '../../../hooks/deprecatedAPI/useFetchRevisions/useFetchRevisions';
import type { AddedProject, MinimalRevision } from '../../../shared/branches/types';

type RevisionOption = SelectOption<string> & {
  revision: MinimalRevision;
  latestRevision?: LatestRevision;
};

//  Individual option for the revision dropdown
const ProjectReleaseRevisionSelectOption: VoidFunctionComponent<RevisionOption> = ({
  value,
  label,
  revision,
  latestRevision,
}) => {
  const revisionTag = compactRevision(revision.locator);
  return (
    <div className={styles.revisionRow}>
      <div className={styles.revisionHashAndLabel}>
        {revisionTag && <span className={styles.hashBadge}>{revisionTag}</span>}
        <div className={styles.revisionLabel}>{label}</div>
      </div>
      {value === latestRevision?.locator && <div className={styles.latestBadge}>latest</div>}
    </div>
  );
};

const revisionToOption = (revision: MinimalRevision, latestRevision?: LatestRevision): RevisionOption => {
  return {
    ...selectOptionFactory(
      revision.locator,
      revision.message ?? revision.locator,
    ),
    //  Include the current and latest revisions updating the addedProject when changed
    revision,
    latestRevision,
  };
};

export type Props = {
  addedProject: AddedProject;
  onUpdateAddedProject: (project: AddedProject) => void;
};

/**
 * A generic representation of a card that allows the user to select a branch and revision for a project.
 */
const BranchRevisionCard: VoidFunctionComponent<Props> = ({ addedProject, onUpdateAddedProject }) => {
  const { onFetchBranches, areBranchesLoading, branches } = useFetchBranches();
  const { onFetchRevisions, areRevisionsLoading, revisions } = useFetchRevisions();

  const branchOptions: SelectOption[] = branches.map((branch) => selectOptionFactory(branch.name));
  const revisionOptions = revisions.map((revision) => revisionToOption(revision, addedProject.latestRevision));

  const onChangeBranch = (branchOption: SelectOption<string> | null) => {
    if (!branchOption) {
      return;
    }

    // When a branch is selected, load revisions and unset the current (or pick latest?)
    onFetchRevisions(addedProject.locator, branchOption.value);

    // Update the added project's selected branch and unset its revision.
    onUpdateAddedProject({ ...addedProject, branch: branchOption.value, revision: undefined });
  };

  const onChangeRevision = (revisionOption: RevisionOption | null) => {
    if (!revisionOption) {
      return;
    }

    // Update the added project's selected revision.
    onUpdateAddedProject({ ...addedProject, revision: revisionOption.revision });
  };

  // Keep track of the revision dropdown's search input.
  const [search, setSearch] = useState('');
  const debouncedFetchRevisions = useDebouncedCallback(onFetchRevisions);

  const onSearchRevisions = (updatedSearch: string) => {
    setSearch(updatedSearch);
    if (!updatedSearch) {
      return;
    }

    debouncedFetchRevisions(addedProject.locator, addedProject.branch, updatedSearch);
  };

  // When revisions are loaded, update the added project's latestRevision.
  useEffect(() => {
    // Only update the latestRevision if the user is NOT searching revisions.
    if (search) {
      return;
    }

    // By default, revisions are returned from the BE in descending order (latest first).
    const latestRevision = revisions[0];
    if (latestRevision) {
      onUpdateAddedProject({ ...addedProject, latestRevision });
    }
  // Only run this effect when the array of available revisions changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisions]);

  return (
    <div className={styles.card}>
      <div className={styles.branchInput}>
        <span>Branch</span>
        <Select
          id="BranchDropdown"
          onMenuOpen={() => onFetchBranches(addedProject.locator)}
          isLoading={areBranchesLoading}
          value={selectOptionFactory(addedProject.branch)}
          onChange={onChangeBranch}
          options={branchOptions}
        />
      </div>
      <div className={styles.revisionInput}>
        <span>Revision</span>
        <Select
          id="RevisionDropdown"
          onMenuOpen={() => onFetchRevisions(addedProject.locator, addedProject.branch)}
          isLoading={areRevisionsLoading}
          value={addedProject.revision
            ? revisionToOption(addedProject.revision, addedProject.latestRevision)
            : null}
          onChange={onChangeRevision}
          options={revisionOptions}
          getOptionLabel={ProjectReleaseRevisionSelectOption}
          inputValue={search}
          onInputChange={onSearchRevisions}
        />
      </div>
    </div>
  );
};

export default BranchRevisionCard;
